import { useState, useEffect, useRef, useCallback } from 'react';
import { startRingback } from './ringback';
import { supabase } from './supabase';
import { holdSession, extendIdleLimit } from './sessionTimeout';
import { notifyStaff } from './notify';

// Module-level flag so code outside the dialer (the sidebar's sign-out) can
// tell whether a call is up without being handed the hook's state.
let _callActive = false;
export function isCallActive() { return _callActive; }

// Presence beats need a signed-in user (the database records who is beating).
// _online is true while this browser is reporting itself available.
let _online = false;
async function sendBeat(available) {
  const { data } = await supabase.auth.getSession();
  if (!data?.session) return; // signed out: nobody to report, and the call would fail
  const { error } = await supabase.rpc('heartbeat', { p_available: available, p_user_agent: navigator.userAgent });
  if (error) console.warn('heartbeat failed:', error.message);
}
// Called just before signing out, while the session still exists, so this
// person stops being rung straight away instead of after the 90s window.
export async function markOffline() {
  if (!_online) return;
  _online = false;
  try { await sendBeat(false); } catch (e) { /* best effort */ }
}

// While the dialer is registered the agent is waiting for inbound calls,
// which produces no mouse or keyboard events. The idle sign-out is stretched
// to this long instead of the normal five minutes.
const DIALER_IDLE_LIMIT_MS = 60 * 60 * 1000;

// An outcome that has not been logged yet survives a reload here.
const PENDING_KEY = 'mm_pending_call';
function readPending(userId) {
  try {
    const raw = window.localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || !p.call || (userId && p.userId && p.userId !== userId)) return null;
    return p.call;
  } catch { return null; }
}
function writePending(userId, call) {
  try {
    if (call) window.localStorage.setItem(PENDING_KEY, JSON.stringify({ userId: userId || null, call }));
    else window.localStorage.removeItem(PENDING_KEY);
  } catch { /* storage blocked: nothing to keep */ }
}

// Calls still waiting to be logged when an incoming call was answered. The
// earlier call is set aside (with whatever was typed about it) and comes back
// to be logged once the incoming call has been logged or skipped.
const HELD_KEY = 'mm_held_calls';
function readHeld(userId) {
  try {
    const p = JSON.parse(window.localStorage.getItem(HELD_KEY) || 'null');
    if (!p || !Array.isArray(p.calls) || (userId && p.userId && p.userId !== userId)) return [];
    return p.calls;
  } catch { return []; }
}
function writeHeld(userId, calls) {
  try {
    if (calls.length) window.localStorage.setItem(HELD_KEY, JSON.stringify({ userId: userId || null, calls }));
    else window.localStorage.removeItem(HELD_KEY);
  } catch { /* storage blocked */ }
}

// YYYY-MM-DD in the agent's own time zone. toISOString() is UTC, which put an
// evening call in Sydney on the previous day.
export function localDate(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Twilio Voice SDK, loaded from a CDN on first use.
//
// Not an npm dependency on purpose. Keeping it out of package.json means the
// lockfile stays untouched, `npm ci` keeps working, and the SDK is only
// fetched by executives who actually place a call rather than by everyone who
// opens the CRM.
// ─────────────────────────────────────────────────────────────────────────────
const VOICE_SDK_URL = 'https://cdn.jsdelivr.net/npm/@twilio/voice-sdk@2.15.0/dist/twilio.min.js';
let _sdkPromise = null;

function loadVoiceSdk() {
  if (window.Twilio && window.Twilio.Device) return Promise.resolve(window.Twilio.Device);
  if (_sdkPromise) return _sdkPromise;

  _sdkPromise = new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = VOICE_SDK_URL;
    el.async = true;
    el.onload = () => {
      if (window.Twilio && window.Twilio.Device) resolve(window.Twilio.Device);
      else { _sdkPromise = null; reject(new Error('The Twilio Voice SDK loaded but looks wrong. Hard refresh the page.')); }
    };
    el.onerror = () => {
      _sdkPromise = null;
      reject(new Error('Could not load the Twilio Voice SDK. Check your connection — some networks block cdn.jsdelivr.net.'));
    };
    document.head.appendChild(el);
  });
  return _sdkPromise;
}

// ─────────────────────────────────────────────────────────────────────────────
// Outbound lines
//
// These used to be a hardcoded array here and a matching one in
// twilio-voice.ts, so adding a number meant editing two files and deploying.
// They now live in public.phone_lines and are read at runtime by both sides.
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchLines() {
  const { data, error } = await supabase
    .from('phone_lines')
    .select('id, label, phone_e164, flag, active')
    .eq('active', true)
    .order('sort_order');
  if (error) throw new Error('Could not load the outbound lines: ' + error.message);
  return data || [];
}

// The default line is simply the first active one by sort order. Any line can
// dial any destination — routing by country prefix was removed because it
// second-guessed the agent, who can always pick a line in the dialer.
export function pickLine(lines) {
  return (lines && lines.length) ? lines[0] : null;
}

export function formatE164(n) {
  if (!n) return '';
  if (n.startsWith('+61') && n.length === 12) return `+61 ${n.slice(3,4)} ${n.slice(4,8)} ${n.slice(8)}`;
  if (n.startsWith('+1')  && n.length === 12) return `+1 ${n.slice(2,5)} ${n.slice(5,8)} ${n.slice(8)}`;
  return n;
}

// ─────────────────────────────────────────────────────────────────────────────
// Calling hours. A soft warning, not a block — the agent can override, and the
// override is worth seeing because it is exactly the behaviour a complaint
// later turns on. Offsets are approximate and ignore DST; good enough to catch
// "it is 3am where you are dialing".
// ─────────────────────────────────────────────────────────────────────────────
const UTC_OFFSETS = [
  { prefix: '+61',  offset: 10 },  // AEST
  { prefix: '+64',  offset: 12 },  // NZST
  { prefix: '+65',  offset: 8  },
  { prefix: '+852', offset: 8  },
  { prefix: '+52',  offset: -6 },
  { prefix: '+1',   offset: -5 },  // spans -5 to -8; the earliest is the risk
];

export function destinationLocalHour(e164) {
  if (!e164) return null;
  const match = UTC_OFFSETS
    .slice()
    .sort((a, b) => b.prefix.length - a.prefix.length)
    .find(o => e164.startsWith(o.prefix));
  if (!match) return null;
  const utcHour = new Date().getUTCHours() + new Date().getUTCMinutes() / 60;
  return ((utcHour + match.offset) % 24 + 24) % 24;
}

export function callingHoursWarning(e164) {
  const hour = destinationLocalHour(e164);
  if (hour === null) return null;
  if (hour >= 8 && hour < 20) return null;
  const h = Math.floor(hour);
  const m = String(Math.floor((hour - h) * 60)).padStart(2, '0');
  return `It is about ${String(h).padStart(2, '0')}:${m} where you are calling. Outside 8am–8pm local.`;
}

// ─────────────────────────────────────────────────────────────────────────────
// useDialer
//
// Owns the Twilio Device for the session. The token comes from the
// twilio-token Edge Function, which derives the agent from the Supabase
// session — there is no password and no name field.
// ─────────────────────────────────────────────────────────────────────────────
export function useDialer({ userId = null } = {}) {
  const [state, setState]   = useState('idle'); // idle | connecting | ready | calling | ringing | oncall | error
  const [error, setError]   = useState(null);
  const [agent, setAgent]   = useState(null);
  // { callSid, to, contactId, companyId, sequenceId, answered, seconds }.
  // Restored from localStorage so a reload does not lose an un-logged outcome.
  const [lastCall, setLastState] = useState(() => readPending(userId));
  const setLast = useCallback((call) => {
    setLastState(call);
    writePending(userId, call);
  }, [userId]);
  const [held, setHeldState] = useState(() => readHeld(userId));
  const heldRef = useRef(held);
  const setHeld = useCallback((calls) => { heldRef.current = calls; setHeldState(calls); writeHeld(userId, calls); }, [userId]);
  // Set the un-logged call aside (with its draft outcome/notes) so answering
  // an incoming call does not throw it away.
  const holdLastCall = useCallback((call, draft) => {
    if (!call) return;
    setHeld([...heldRef.current, { call, draft: draft || null }]);
  }, [setHeld]);
  const [lines, setLines]   = useState([]);     // from public.phone_lines
  // The call currently ringing this browser, with whatever twilio-inbound
  // could work out about the caller. Null when nothing is ringing.
  const [incoming, setIncoming] = useState(null);
  // The inbound call in progress (caller details from the screen pop), so the
  // panel can show who is on the line even when it was opened by answering.
  const [inboundCall, setInboundCall] = useState(null);
  // Bumped by reconnect() to tear the device down and set it up again.
  const [attempt, setAttempt] = useState(0);

  const deviceRef = useRef(null);
  const quietSwapRef = useRef(false);
  const callRef   = useRef(null);
  const metaRef   = useRef(null);
  const startRef  = useRef(null);
  const incomingRef = useRef(null);

  const getToken = useCallback(async () => {
    // getSession() renews an expired sign-in first. If the server still says
    // "not signed in" (the sign-in went stale while the laptop slept or the
    // tab was in the background), refresh it once and try again.
    await supabase.auth.getSession();
    let { data, error } = await supabase.functions.invoke('twilio-token');
    if (error && error.context?.status === 401) {
      const { error: refreshErr } = await supabase.auth.refreshSession();
      if (refreshErr) throw new Error('Your sign-in has expired. Sign out and sign in again to use the dialer.');
      ({ data, error } = await supabase.functions.invoke('twilio-token'));
    }
    if (error) {
      // Show the function's own reason (e.g. "Calling is not configured")
      // rather than the generic "non-2xx status code".
      let msg = error.message || 'Could not get a calling token';
      try { const body = await error.context.json(); if (body?.error) msg = body.error; } catch (e) { /* keep generic */ }
      const status = error.context?.status;
      throw new Error(status ? `${msg} (twilio-token ${status})` : msg);
    }
    if (!data?.token) throw new Error(data?.error || 'Calling token was empty');
    return data;
  }, []);

  // Set the device up once, on mount.
  useEffect(() => {
    let cancelled = false;
    let device;
    let retryTimer;

    (async () => {
      // A quiet swap (releaseDevice) keeps showing "ready": a Call pressed
      // meanwhile waits for the new device instead of meeting a grey button.
      const quiet = quietSwapRef.current;
      quietSwapRef.current = false;
      if (!quiet) setState('connecting');
      try {
        // Microphone permission is NOT requested here. This effect runs as soon
        // as the CRM loads, and prompting every executive on page load for a
        // feature most of them are not about to use is hostile. It is requested
        // in placeCall instead, at the moment it is actually needed.
        const Device = await loadVoiceSdk();
        if (cancelled) return;

        const available = await fetchLines();
        if (cancelled) return;
        if (!available.length) throw new Error('No outbound line is set up. Ask your DBA to add one under Dialer Lines.');
        setLines(available);

        const { token, agentName } = await getToken();
        if (cancelled) return;
        setAgent(agentName);

        device = new Device(token, { logLevel: 'warn', codecPreferences: ['opus', 'pcmu'] });
        deviceRef.current = device;

        device.on('registered', () => {
          if (cancelled) return;
          setError(null);
          // Re-registering mid-call must not hide the call controls.
          setState(callRef.current ? 'oncall' : 'ready');
        });
        // Twilio drops the registration on a network blip or a sleeping
        // laptop. Without this the dialer sat on "Connecting" until a reload.
        device.on('unregistered', () => {
          if (cancelled) return;
          if (!callRef.current) setState('connecting');
          clearTimeout(retryTimer);
          retryTimer = setTimeout(() => {
            if (!cancelled && device.state === 'unregistered') device.register().catch(() => { /* error handler reports it */ });
          }, 5000);
        });

        // ── Inbound ────────────────────────────────────────────────────────
        // twilio-inbound has already worked out who is calling and attached it
        // as custom parameters, so the screen pop is populated before the
        // agent decides whether to answer. An inbound call is nearly always a
        // callback, which makes it the warmest call of the day — answering it
        // by name is the entire point of this.
        device.on('incoming', (call) => {
          if (cancelled) return;
          // Already on a call: let it ring out to voicemail rather than
          // dumping the current conversation.
          if (callRef.current) { try { call.reject(); } catch { /* gone */ } return; }

          const p = call.customParameters || new Map();
          const get = (k) => (typeof p.get === 'function' ? p.get(k) : p[k]) || '';

          incomingRef.current = call;
          setIncoming({
            // The browser's own CallSid is the child leg. The parent is what
            // the calls row is keyed on, so dispositions land on the right row.
            callSid:      get('parentCallSid') || call.parameters?.CallSid || null,
            matchType:    get('matchType') || 'unknown',
            from:         get('fromNumber') || call.parameters?.From || '',
            lineLabel:    get('lineLabel'),
            contactId:    get('contactId') || null,
            contactName:  get('contactName'),
            contactTitle: get('contactTitle'),
            companyName:  get('companyName'),
          });

          const clear = () => { incomingRef.current = null; setIncoming(null); };
          call.on('cancel', clear);     // caller gave up
          call.on('reject', clear);
          call.on('disconnect', clear);
        });

        // A failed call is not a dead line. This used to switch the whole
        // dialer to "Not available" (Call button disabled) after any one call
        // failed, which looked like a limit on calls until the page reloaded.
        // Only a device that is no longer registered is really unavailable.
        device.on('error', (e, call) => {
          if (cancelled) return;
          setError(describeError(e));
          if (call || callRef.current) return;   // the call's own handlers tidy up
          if (device.state === 'registered') return;
          setState('error');
        });

        // The bug that killed the old dialer after an hour.
        device.on('tokenWillExpire', async () => {
          try {
            const fresh = await getToken();
            device.updateToken(fresh.token);
          } catch (e) {
            setError('Session expired. Reload the page to keep calling.');
            setState('error');
          }
        });

        await device.register();
      } catch (e) {
        if (cancelled) return;
        setError(e.message);
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      try { device?.destroy(); } catch { /* already gone */ }
      deviceRef.current = null;
    };
  }, [getToken, attempt]);

  // Start the dialer again from scratch (new token, new device).
  const reconnect = useCallback(() => {
    if (callRef.current) return;
    setError(null);
    setAttempt(a => a + 1);
  }, []);

  // Twilio's Device can be left believing a call is still active after one
  // that failed before it connected: it only lets go of a call once the call
  // is closed, and disconnect() does nothing to a call that never got past
  // "pending". Every later connect() then fails with "A Call is already
  // active" until the page is reloaded. This does what the reload did: a new
  // device, resolved once it is registered.
  // The registered device, waiting (up to 15s) while one is being set up.
  // `not` skips a device that is being replaced.
  const readyDevice = useCallback(async (not = null) => {
    const until = Date.now() + 15000;
    for (;;) {
      const d = deviceRef.current;
      if (d && d !== not && d.state === 'registered') return d;
      if (Date.now() > until) throw new Error('The dialer could not reconnect. Press Reconnect, then try again.');
      await new Promise(r => setTimeout(r, 200));
    }
  }, []);
  const freshDevice = useCallback(async () => {
    const old = deviceRef.current;
    setAttempt(a => a + 1);
    return readyDevice(old);
  }, [readyDevice]);
  // Free once our call has ended: close the SDK's call too, and if the device
  // still thinks it is busy, replace it now so the next Call works first time.
  const releaseDevice = useCallback((call) => {
    try { if (call && typeof call.status === 'function' && call.status() !== 'closed') call.disconnect(); } catch { /* gone */ }
    setTimeout(() => {
      const d = deviceRef.current;
      if (d && d.isBusy && !callRef.current) { quietSwapRef.current = true; setAttempt(a => a + 1); }
    }, 1500);
  }, []);

  // ── Place a call ───────────────────────────────────────────────────────────
  const placeCall = useCallback(async ({ to, contactId, companyId, sequenceId, line }) => {
    // Normally the registered device. If it is being replaced (releaseDevice)
    // or still registering, wait for it rather than refusing the call.
    let device = deviceRef.current;
    if (!device || device.state !== 'registered') device = await readyDevice();

    const e164 = String(to).replace(/[^\d+]/g, '');
    if (!/^\+[1-9]\d{6,14}$/.test(e164)) throw new Error('That is not a valid international number');

    // Asked for here, on the first call of the session, rather than on page
    // load. The browser remembers the grant, so this prompts once.
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach(t => t.stop());
    } catch {
      throw new Error('Microphone access is blocked. Allow it in your browser settings, then try again.');
    }

    const chosen = line || pickLine(lines);
    if (!chosen) throw new Error('No outbound line is available');
    metaRef.current = {
      to: e164, contactId, companyId, sequenceId,
      lineId: chosen.id, lineLabel: chosen.label, fromNumber: chosen.phone_e164,
    };
    startRef.current = Date.now();
    setLast(null);
    setError(null);
    setState('calling');

    if (device.isBusy && !callRef.current) {
      try { device = await freshDevice(); }
      catch (e) { setState('error'); throw e; }
    }

    const params = {
      To:         e164,
      Line:       chosen.id,
      // Advisory only; twilio-voice takes the agent from the token.
      AgentId:    (await supabase.auth.getSession()).data.session?.user?.id || '',
      ContactId:  contactId  || '',
      CompanyId:  companyId  || '',
      SequenceId: sequenceId || '',
    };
    let call;
    try {
      try {
        call = await device.connect({ params });
      } catch (e) {
        if (!/already active/i.test(String(e?.message || e))) throw e;
        device = await freshDevice();
        call = await device.connect({ params });
      }
    } catch (e) {
      // Without this the panel stayed on "Dialing…" with every button dead.
      setState(device.state === 'registered' ? 'ready' : 'error');
      throw new Error(describeError(e));
    }

    callRef.current = call;
    _callActive = true;
    setState('calling');   // a replaced device reports "ready" while it registers
    let answered = false;

    // Stop the five-minute inactivity timer signing the agent out mid-call.
    const releaseHold = holdSession();

    // Ringing in the headset until they answer. If the far network sends its
    // own ringing (early media), that is what they hear instead.
    const stopRing = startRingback();
    call.on('ringing', (hasEarlyMedia) => { setState('ringing'); if (hasEarlyMedia) stopRing(); });
    call.on('accept', () => {
      stopRing();
      // answerOnBridge is set in twilio-voice, so this means the far end
      // actually picked up — not merely that Twilio accepted the leg.
      answered = true;
      startRef.current = Date.now();
      setState('oncall');
    });

    const finish = () => {
      stopRing();
      releaseHold();
      const seconds = answered ? Math.round((Date.now() - startRef.current) / 1000) : 0;
      setLast({
        ...metaRef.current,
        callSid: call.parameters?.CallSid || null,
        answered,
        seconds,
      });
      callRef.current = null;
      _callActive = false;
      setState('ready');
      releaseDevice(call);
    };

    call.on('disconnect', finish);
    call.on('cancel', finish);
    call.on('error', e => { setError(describeError(e)); finish(); });

    return call;
  }, [lines, setLast, freshDevice, readyDevice, releaseDevice]);

  // ── Presence ───────────────────────────────────────────────────────────────
  // twilio-inbound only rings identities seen recently, so a closed tab or a
  // sleeping laptop sends callers to voicemail instead of ringing an empty
  // desk. This also removes any need for a business-hours table: at 3am nobody
  // is beating, so the call takes a message because that is true.
  // Keyed on a derived boolean rather than `state`: with `state` as the
  // dependency every ready -> calling -> oncall step ran the cleanup, which
  // marked the agent unavailable for a moment on every call.
  const online = state === 'ready' || state === 'calling' || state === 'ringing' || state === 'oncall';
  useEffect(() => {
    if (!online) return;
    // Waiting for an inbound call is work: stretch the idle sign-out.
    const releaseIdle = extendIdleLimit(DIALER_IDLE_LIMIT_MS);

    let stopped = false;
    const beat = (available) => { _online = available; return sendBeat(available).catch(() => {}); };

    beat(true);
    // 30s against the 90s staleness window in online_agents(), so one missed
    // beat is survivable.
    const timer = setInterval(() => { if (!stopped) beat(true); }, 30000);

    // Best-effort "I am gone" so they stop being rung immediately rather than
    // after the window expires. Not guaranteed to land, which is exactly why
    // the staleness window exists as well.
    const leave = () => beat(false);
    window.addEventListener('pagehide', leave);

    return () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener('pagehide', leave);
      releaseIdle();
      leave();
    };
  }, [online]);

  // ── Answer an inbound call ─────────────────────────────────────────────────
  const acceptIncoming = useCallback(async () => {
    const call = incomingRef.current;
    const meta = incoming;
    if (!call) return false;

    callRef.current = call;
    _callActive = true;
    incomingRef.current = null;
    setIncoming(null);

    // Inbound calls are logged against the same shape as outbound, so the
    // disposition panel and logCallOutcome need no special case.
    metaRef.current = {
      to: meta?.from || '', from: meta?.from || '', contactId: meta?.contactId || null,
      // Kept so the panel can still say who it was once the call has ended.
      contactName: meta?.contactName || '', contactTitle: meta?.contactTitle || '', companyName: meta?.companyName || '',
      companyId: null, sequenceId: null,
      lineId: null, lineLabel: meta?.lineLabel || null,
      direction: 'inbound', callSidOverride: meta?.callSid || null,
    };
    startRef.current = Date.now();
    setLast(null);

    const releaseHold = holdSession();
    let answered = true;   // accepting an inbound call is the connection

    const finish = () => {
      releaseHold();
      setInboundCall(null);
      setLast({
        ...metaRef.current,
        callSid: metaRef.current?.callSidOverride || call.parameters?.CallSid || null,
        answered,
        seconds: Math.round((Date.now() - startRef.current) / 1000),
      });
      callRef.current = null;
      _callActive = false;
      setState('ready');
      releaseDevice(call);
    };

    call.on('disconnect', finish);
    call.on('cancel', () => { answered = false; finish(); });
    call.on('error', (e) => { setError(describeError(e)); finish(); });

    try {
      // Permission is normally already granted from an earlier outbound call,
      // but an agent who has only ever received calls will not have been asked.
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach(t => t.stop());
    } catch {
      setError('Microphone access is blocked, so the call cannot be answered. Allow it in your browser settings.');
      try { call.reject(); } catch { /* gone */ }
      releaseHold();
      callRef.current = null;
      _callActive = false;
      setState('ready');
      return false;
    }

    call.accept();
    setInboundCall(meta || {});
    setState('oncall');

    // twilio-inbound records the call before anyone answers, with no agent on
    // it. Claim it so this agent can log the outcome and it shows in their
    // history, and pick up the company the caller was matched to.
    if (meta?.callSid) {
      const { data: claimed } = await supabase.rpc('claim_inbound_call', { p_call_sid: meta.callSid });
      const row = Array.isArray(claimed) ? claimed[0] : claimed;
      if (row && metaRef.current) {
        metaRef.current.contactId = metaRef.current.contactId || row.contact_id || null;
        metaRef.current.companyId = row.company_id || null;
      }
    }
    return true;
  }, [incoming, setLast, releaseDevice]);

  const rejectIncoming = useCallback(() => {
    const call = incomingRef.current;
    incomingRef.current = null;
    setIncoming(null);
    // Rejecting sends the caller to voicemail rather than hanging up on them:
    // twilio-inbound's dial action treats a rejected leg as no answer.
    try { call?.reject(); } catch { /* already gone */ }
  }, []);

  const hangUp = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    try {
      const st = typeof call.status === 'function' ? call.status() : '';
      if (['pending', 'connecting', 'ringing'].includes(st) && call.cancel) call.cancel();
      else call.disconnect();
    } catch {
      deviceRef.current?.disconnectAll();
    }
  }, []);

  const setMuted = useCallback((muted) => {
    try { callRef.current?.mute(muted); } catch { /* no active call */ }
  }, []);

  const sendDigit = useCallback((d) => {
    try { callRef.current?.sendDigits(d); } catch { /* no active call */ }
  }, []);

  // Finish with the current call. If an earlier call was set aside, it comes
  // back to be logged; its draft is returned so the panel can restore it.
  const clearLastCall = useCallback(() => {
    const [next, ...rest] = heldRef.current;
    setLast(next ? next.call : null);
    if (next) setHeld(rest);
    return next ? (next.draft || {}) : null;
  }, [setLast, setHeld]);

  return { state, error, agent, lines, lastCall, incoming, inboundCall, heldCount: held.length,
           placeCall, acceptIncoming, rejectIncoming, holdLastCall,
           hangUp, setMuted, sendDigit, clearLastCall, reconnect };
}

function describeError(e) {
  const code = e?.code || e?.originalError?.code;
  const map = {
    31003: 'No media path — check the network or firewall',
    31005: 'Connection lost',
    31201: 'Microphone unavailable',
    31208: 'Microphone permission denied',
    31402: 'No audio input device',
    13223: 'That country is blocked in Twilio Geo Permissions',
    13227: 'That country is blocked in Twilio Geo Permissions',
    21210: 'The caller ID is not verified on this Twilio account',
  };
  if (code && map[code]) return `${map[code]} (${code})`;
  return (e?.message || String(e)) + (code ? ` (${code})` : '');
}

// ─────────────────────────────────────────────────────────────────────────────
// Outcome logging
//
// Twilio decides WHAT happened (attempted / connected / voicemail). The agent
// decides WHAT IT MEANT (the outcome). That split is the whole point: the part
// a human used to guess at is now measured.
//
// Uses the existing activities vocabulary so every chart in ExecutiveAnalytics
// keeps working unchanged.
// ─────────────────────────────────────────────────────────────────────────────
export const CALL_OUTCOMES = [
  ['📅 Meeting Booked',  'Meeting Booked'],
  ['👍 Positive',        'Positive Response'],
  ['📞 Callback',        'Callback Requested'],
  ['📭 Voicemail',       'Voicemail Left'],
  ['🔇 No Answer',       'No Answer'],
  ['📪 No Response',     'No Response'],
  ['✗ Not Interested',   'Not Interested'],
];

export function callActivityType({ answered, seconds, outcome }) {
  if (outcome === 'Meeting Booked')  return 'Meeting Booked';
  if (outcome === 'Voicemail Left')  return 'Call - Voicemail';
  // A "connected" call under 20 seconds is almost always a hang-up or a
  // switchboard, not a conversation.
  if (answered && seconds >= 20)     return 'Call Connected';
  return 'Call Attempted';
}

// ─────────────────────────────────────────────────────────────────────────────
// Sequence / company effects of an outcome
//
// The same rules as the sequence card in ExecutiveApp (logOutcome), kept here
// so a call logged from the dialer moves the sequence on exactly once and the
// executive does not log the same touch a second time on the card.
// ExecutiveApp can call this too, after inserting its own activity.
//
//   6-touch cadence: TOUCH_DAY is the day each touch falls on, from touch 1.
//   Meeting Booked     -> sequence Converted, company Meeting Scheduled
//   Not Interested     -> sequence Not Interested, company Disqualified
//   Referred Elsewhere -> sequence Completed
//   last touch logged  -> sequence Completed
//   otherwise          -> next touch, due after the gap in the cadence
//   Positive Response  -> company Engaged
//   Flag for Director  -> company flagged
//   Meeting Booked / Not Interested / Flag for Director notify directors.
// ─────────────────────────────────────────────────────────────────────────────
export const TOUCH_DAY = [0, 3, 6, 9, 14, 18];
export const MAX_TOUCH = TOUCH_DAY.length;
export const gapBefore = (touch) => (touch <= 1 ? 0 : TOUCH_DAY[touch - 1] - TOUCH_DAY[touch - 2]);

export async function applyOutcomeToPipeline({ outcome, profile, companyId = null, sequenceId = null, notes = '' }) {
  const now = new Date().toISOString();
  let company = null;
  let sequence = null;

  if (sequenceId) {
    const { data, error } = await supabase.from('sequences')
      .select('id, status, current_touch, company_id').eq('id', sequenceId).maybeSingle();
    if (error) return { error };
    // Only a live sequence moves. A paused or finished one is left alone.
    if (data && data.status === 'Active') sequence = data;
  }
  const coId = companyId || sequence?.company_id || null;
  if (coId) {
    const { data } = await supabase.from('companies').select('id, name, status').eq('id', coId).maybeSingle();
    company = data || null;
  }

  // Company: only write what this outcome changes.
  if (company) {
    const update = {};
    if (outcome === 'Meeting Booked') update.status = 'Meeting Scheduled';
    if (outcome === 'Not Interested') update.status = 'Disqualified';
    if (outcome === 'Positive Response' && company.status !== 'Engaged') update.status = 'Engaged';
    if (outcome === 'Flag for Director') update.flagged_for_director = true;
    if (Object.keys(update).length) {
      const { error } = await supabase.from('companies').update({ ...update, updated_at: now }).eq('id', company.id);
      if (error) return { error: new Error('Updating the company failed: ' + error.message) };
    }
  }

  let sequenceEnded = false;
  let nextTouch = null;
  if (sequence) {
    const lastTouch = (sequence.current_touch || 1) >= MAX_TOUCH;
    const endStatus = outcome === 'Meeting Booked' ? 'Converted'
                    : outcome === 'Not Interested' ? 'Not Interested'
                    : outcome === 'Referred Elsewhere' ? 'Completed'
                    : lastTouch ? 'Completed' : null;
    const seqUpdate = { last_outcome: outcome, updated_at: now };
    if (endStatus) {
      seqUpdate.status = endStatus;
      sequenceEnded = true;
    } else {
      nextTouch = (sequence.current_touch || 1) + 1;
      seqUpdate.current_touch = nextTouch;
      seqUpdate.next_action_date = localDate(gapBefore(nextTouch));
      seqUpdate.next_action_type = nextTouch % 2 === 0 ? 'Call' : 'Email';
    }
    const { error } = await supabase.from('sequences').update(seqUpdate).eq('id', sequence.id);
    if (error) return { error: new Error('Updating the sequence failed: ' + error.message) };
  }

  if (outcome === 'Flag for Director' || outcome === 'Meeting Booked' || outcome === 'Not Interested') {
    await notifyStaff(['director', 'manager'], {
      type: outcome === 'Meeting Booked' ? 'meeting_booked' : outcome === 'Flag for Director' ? 'flag_director' : 'not_interested',
      title: `${outcome} — ${company?.name || 'a company'}`,
      body: `${profile?.full_name || 'An executive'} logged: ${outcome}${notes && outcome === 'Flag for Director' ? ` — “${String(notes).trim()}”` : ''}`,
      companyId: company?.id || null, relatedExecId: profile?.id || null,
    });
  }

  return { sequenceAdvanced: !!sequence, sequenceEnded, nextTouch };
}

// Attaches the agent's disposition to the measured call record. Goes through
// the log_call_outcome function (PHASE_15), because the browser can no longer
// write calls directly. Falls back to the old upsert only when that function
// has not been installed yet, so the dialer keeps working before the SQL runs.
async function writeDisposition({ call, outcome, notes, activityId, profile }) {
  const { error } = await supabase.rpc('log_call_outcome', {
    p_call_sid:    call.callSid,
    p_disposition: outcome,
    p_notes:       notes || null,
    p_activity_id: activityId || null,
  });
  if (!error) return null;
  const msg = error.message || '';
  const missing = error.code === 'PGRST202' || error.code === '42883'
    || (/log_call_outcome/.test(msg) && /find|exist/i.test(msg));
  if (!missing) return error;

  const { error: upErr } = await supabase.from('calls').upsert({
    call_sid:    call.callSid,
    agent_id:    profile.id,
    disposition: outcome,
    notes:       notes || null,
    ...(activityId ? {
      activity_id: activityId,
      contact_id:  call.contactId  || null,
      company_id:  call.companyId  || null,
      sequence_id: call.sequenceId || null,
    } : {}),
    // market_line is written by twilio-status only (the line id). On an
    // inbound row to_number is OUR line, so it is never overwritten here.
    ...(call.direction === 'inbound' ? {} : { to_number: call.to }),
  }, { onConflict: 'call_sid' });
  return upErr || null;
}

// Returns { error } when nothing was saved, otherwise
// { activityId, activityType, pipeline, warning? }. A warning means the
// outcome was saved but something after it was not, and must be shown.
export async function logCallOutcome({ call, outcome, notes, profile }) {
  const activity_type = callActivityType({
    answered: call.answered, seconds: call.seconds, outcome,
  });
  // calls.call_sid is NOT NULL: a call that never reached Twilio has no row.
  const noRecord = !call.callSid;
  const noRecordMsg = 'This call never reached Twilio, so there is no call record to attach the outcome to.';

  // A keypad call with no contact has no place in the pipeline: an activity
  // row with no company, contact or sequence would sit in the Activity Log
  // attached to nothing and skew per-contact analytics. The outcome and notes
  // go on the call record instead, which is where operations reporting reads
  // them from.
  if (!call.contactId && !call.companyId) {
    if (noRecord) return { error: new Error(noRecordMsg + ' Nothing was logged.') };
    const error = await writeDisposition({ call, outcome, notes, profile });
    return error ? { error } : { activityType: activity_type, pipeline: false };
  }

  // The touch this call was, read fresh so a stale card cannot mislabel it.
  let touch = null;
  if (call.sequenceId) {
    const { data } = await supabase.from('sequences').select('current_touch, status').eq('id', call.sequenceId).maybeSingle();
    if (data && data.status === 'Active') touch = data.current_touch || null;
  }

  const { data: activity, error: actErr } = await supabase
    .from('activities')
    .insert({
      owner_id:      profile.id,
      company_id:    call.companyId  || null,
      contact_id:    call.contactId  || null,
      sequence_id:   call.sequenceId || null,
      activity_date: localDate(),
      activity_type,
      outcome,
      touch_number:  touch,
      body:          notes || null,
    })
    .select('id')
    .single();

  if (actErr) return { error: actErr };

  const warnings = [];
  if (noRecord) warnings.push(noRecordMsg);
  else {
    const dErr = await writeDisposition({ call, outcome, notes, activityId: activity.id, profile });
    if (dErr) warnings.push('The outcome is logged, but the call record was not updated: ' + dErr.message);
  }

  // Move the sequence on, exactly as the sequence card would.
  const pipe = await applyOutcomeToPipeline({
    outcome, profile, companyId: call.companyId || null, sequenceId: call.sequenceId || null, notes,
  });
  if (pipe.error) warnings.push(pipe.error.message);

  return {
    activityId: activity.id, activityType: activity_type, pipeline: true,
    sequenceEnded: !!pipe.sequenceEnded, nextTouch: pipe.nextTouch || null,
    warning: warnings.length ? warnings.join(' ') : null,
  };
}

export async function suppressNumber({ phone_e164, reason, notes, profile }) {
  return supabase.from('call_suppression').insert({
    phone_e164, reason, notes: notes || null, added_by: profile.id, source: 'Agent request',
  });
}
